import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import SeeMoreButton from 'components/SeeMoreButton';
import {
  calculateMaxChars,
  SNIPPET_NOT_FOUND_TEXT,
  splitTextIntoParagraphs,
} from 'components/SeeMoreButton/helpers';
import { setExpandedEvidence } from 'core/store/evidenceSlice';
import { useAppSelector } from 'core/store/hooks';
import CopyToClipboardButton from 'pages/PlaygroundV2Page/Summary/components/CopyToClipboardButton';
import { useDispatch } from 'react-redux';
import { trackEvent } from 'utils/Plausible';
import { DARK_GREY, DARKER_GREY } from 'utils/theme';
import ReadAloudButton from '../../../../../../components/ReadAloudButton';
import FeedbackAction from '../../FeedbackAction';

const getTextToRead = (evidence: Source) =>
  `${evidence.title ?? 'Headline not found'}. ${
    evidence.evidenceSnippet ?? SNIPPET_NOT_FOUND_TEXT
  }`;

const getTextToCopy = (evidence: Source) => evidence.url ?? '';

const getHeadline = (evidence: Source) => evidence.title || 'Headline not found';

interface ClaimEvidenceBodyProps {
  evidence: Source;
  claim: Claim;
}

const ClaimEvidenceBody = ({ evidence, claim }: ClaimEvidenceBodyProps) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));

  const expandedEvidence = useAppSelector((state) => state.evidence.expandedEvidence);

  const lines = isMobile ? 4 : 3;
  const MAX_CHAR = calculateMaxChars(lines);
  const text = evidence.evidenceSnippet || SNIPPET_NOT_FOUND_TEXT;
  const isSnippetNotFound = text === SNIPPET_NOT_FOUND_TEXT;
  const isTruncated = text.length > MAX_CHAR;
  const paragraphs = splitTextIntoParagraphs(text);

  const headline = getHeadline(evidence);
  const isExpanded = expandedEvidence === headline;

  const toggleExpand = () => {
    trackEvent('Snippet Expanded');
    dispatch(setExpandedEvidence(isExpanded ? null : headline));
  };

  const renderExpandedText = () =>
    paragraphs.map((paragraph, key) => (
      <span key={key} style={{ marginBottom: '10px', display: 'block' }}>
        {paragraph}
        <br />
      </span>
    ));

  const renderTruncatedText = () => `${paragraphs[0]?.slice(0, MAX_CHAR)}...`;

  const renderTextContent = () => {
    if (isTruncated) {
      return isExpanded ? renderExpandedText() : renderTruncatedText();
    }
    return <span style={{ paddingRight: '8px' }}>{text}</span>;
  };

  return (
    <Box
      sx={{
        background: 'rgba(241, 242, 251, 0.6)',
        borderRadius: '8px',
        marginLeft: {
          mobile: 0,
          tablet: 0,
        },

        marginRight: {
          mobile: 0,
          tablet: 0,
        },
        padding: {
          mobile: '12px',
          tablet: '16px',
          desktop: '16px 95px 16px 30px',
        },
      }}
    >
      <Box
        onClick={toggleExpand}
        sx={{
          cursor: 'pointer',
          color: DARKER_GREY,
          pb: '12px',
        }}
      >
        <Typography
          fontSize="18px"
          sx={{
            display: 'inline',
            fontStyle: isSnippetNotFound ? 'italic' : 'normal',
            color: isSnippetNotFound ? DARK_GREY : 'inherit',
          }}
        >
          {renderTextContent()}{' '}
          {text && text.length > MAX_CHAR && (
            <Box component="span" sx={{ display: 'inline' }}>
              <SeeMoreButton
                isExpanded={expandedEvidence === evidence.title}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand();
                }}
              />
            </Box>
          )}
        </Typography>
      </Box>

      <Stack flexDirection="row">
        <ReadAloudButton text={getTextToRead(evidence)} />
        <CopyToClipboardButton textToCopy={getTextToCopy(evidence)} />
        <FeedbackAction claim={claim} source={evidence} />
      </Stack>
    </Box>
  );
};

export default ClaimEvidenceBody;
